import React, { useMemo } from "react";

import { Button, Row } from "@narmi/design_system";
import { modules } from "byzantine";
import { v4 as uuidv4 } from "uuid";
import ReactMarkdown from "react-markdown";
import { useLocalization } from "@fluent/react";

import HeaderWithSteps from "../../../../../../../components/HeaderWithSteps";
import DomesticWireAdditionalInfo from "../../../../../../../components/transfer/wires/DomesticWireAdditionalInfo";

import styles from "./Review.module.scss";

type Props = {
  onSubmit: (idempotencyKey: UUID) => void;
  loading: boolean;
  goBack: () => void;
};

function Review({ onSubmit, loading, goBack }: Props) {
  const { l10n } = useLocalization();
  const idempotencyKey = useMemo(() => uuidv4() as UUID, []);
  const details = modules.wireTemplates.useReviewDetails();

  if (!details) {
    return null;
  }
  const { sections, disclosure, requiresDualApproval } = details;
  const sanitizedDisclosure =
    disclosure?.replace(/[\u2068\u2069]/g, "") ?? null;

  const buttonLabel = requiresDualApproval
    ? l10n.getString("button-submit-approval")
    : l10n.getString("button-make-payment");

  return (
    <div className={styles.wireViaTemplateReviewContainer}>
      <div className={styles.backButtonRow}>
        <div className={styles.headerContainer}>
          <HeaderWithSteps headerText={l10n.getString("heading-review-wire")} />
        </div>
      </div>
      {sections.map(({ header, rows }, i) => (
        <div key={`section-${header}-${i}`}>
          <p className={styles.reviewSubHeader}>{header}</p>
          <div className={styles.card}>
            {rows.map(({ header: rowHeader, value }, j) => {
              return (
                <div key={`row-${rowHeader}-${j}`}>
                  <p className={styles.key}>{rowHeader}</p>
                  <p className={styles.value}>{value}</p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <DomesticWireAdditionalInfo />
      {sanitizedDisclosure ? (
        <div className="fontColor--secondary">
          <ReactMarkdown
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {sanitizedDisclosure}
          </ReactMarkdown>
        </div>
      ) : null}
      <div className={styles.buttonRowContainer}>
        <Row alignItems="center">
          <Row.Item>
            <Button
              label={l10n.getString("button-back")}
              kind="negative"
              onClick={goBack}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={buttonLabel}
              onClick={() => onSubmit(idempotencyKey)}
              isLoading={loading}
              disabled={loading}
            />
          </Row.Item>
        </Row>
      </div>
    </div>
  );
}

export default React.memo(Review);
